import React from 'react';
import { Row, Button } from 'reactstrap';
import { AgGridReact } from 'ag-grid-react/lib/agGridReact';
import { throttle, isEmpty } from 'lodash-es';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileInvoiceDollar, faQuestionCircle } from '@fortawesome/free-solid-svg-icons';
import { httpClient } from '@s20/services/httpClient';
import { createClientSideGridOptions, TextFilterDefaults } from '../common/dataGrid/DataGrid';
import DataGridSelectFilter from '../common/dataGrid/DataGridSelectFilter';
import DataGridSelectFloatingFilter from '../common/dataGrid/DataGridSelectFloatingFilter';
import CommonContext, { agGridConstants, ApiRoutes } from '../Common';
import SlideForm from '../common/forms/SlideForm';
import { SmallButton, toasty } from '../common/forms/FormElements';

export default class ChargeTypeGroupSelect extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);
        this.formRef = React.createRef();
        this.state = {
            customerId: '',
            chargeTypes: [],
            loading: true,
            omissions: [],
            refreshing: false,
            rowData: [],
            selectedRows: [],
            show: false,
        };

        this.onSelectionChanged = this.onSelectionChanged.bind(this);
        this.setupGrid = this.setupGrid.bind(this);
        this.refreshChargeTypes = throttle(this.refreshChargeTypes, 2000);
    }

    componentDidMount() {
        this.populateState();
    }

    onAdd = () => {
        const { selectedRows } = { ...this.state };
        this.setState({ show: false }, () => {
            this.props.onAddCallback(selectedRows);
        });
        this.context.setFormOpened(false);
    };

    onClose = () => {
        this.setState({ show: false, formValidated: false });
        this.context.setFormOpened(false);
    };

    onSelectionChanged = (event) => {
        const selections = event.api.getSelectedRows() ?? [];
        this.setState({ selectedRows: selections });
    };

    getChargeTypeGroups = async (omissions = [], isProjectContract) => {
        let chargeTypeGroups = await httpClient.js(
            ApiRoutes.typeAheads.chargeTypeGroups(),
        );

        if (!isEmpty(omissions)) {
            chargeTypeGroups = chargeTypeGroups.filter((ct) => (omissions ?? []).includes(ct.value) === false) ?? [];
        }

        if (isProjectContract === false) {
            chargeTypeGroups = chargeTypeGroups.filter((ct) => !ct.hasRentalChargeTypes);
        }

        this.setState({
            rowData: chargeTypeGroups,
            selectedRows: [],
            refreshing: false,
            loading: false,
        });
    };

    setupGrid = ({ chargeTypes }) => {
        const gridOptions = createClientSideGridOptions(this);

        const filterOptions = { ...TextFilterDefaults, suppressFilterButton: true };

        gridOptions.headerHeight = 28;
        gridOptions.rowMultiSelectWithClick = true;
        gridOptions.overlayNoRowsTemplate = 'No charge type groups to display. You\'ve either selected all the available groups, or no groups were found.';

        gridOptions.components = {
            selectFilter: DataGridSelectFilter,
            selectFloatingFilter: DataGridSelectFloatingFilter,
        };

        const chargeTypesFilterParams = {
            suppressFilterButton: true,
            options: chargeTypes,
            optionsLabel: 'label',
            optionsValue: 'value',
        };

        gridOptions.columnDefs = [
            {
                colId: 'Description',
                sortable: true,
                headerName: 'Description',
                field: 'label',
                filter: agGridConstants.columnFilterTypes.text,
                floatingFilter: true,
                filterParams: filterOptions,
                sort: { direction: 'asc', priority: 0 },
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
            },
            {
                sortable: false,
                headerName: 'Charge Types',
                field: 'chargeTypeIds',
                valueFormatter: ({ data }) => data?.chargeTypes ?? '',
                filter: 'selectFilter',
                floatingFilter: true,
                filterParams: chargeTypesFilterParams,
                floatingFilterComponent: 'selectFloatingFilter',
                floatingFilterComponentParams: chargeTypesFilterParams,
            },
        ];

        return { gridOptions };
    };

    open = async (omissions, isProjectContract) => {
        await this.getChargeTypeGroups(omissions, isProjectContract);
        this.context.setFormOpened(true);
        this.setState({
            show: true,
            omissions,
        });
    };

    async populateState() {
        const chargeTypes = await httpClient.js(ApiRoutes.typeAheads.chargeTypes());

        const { gridOptions } = this.setupGrid({ chargeTypes });

        gridOptions.rowData = [];

        this.setState({
            loading: false,
            gridOptions,
        });
    }

    refreshChargeTypes = async () => {
        const { omissions } = { ...this.state };
        await this.getChargeTypeGroups(omissions);
        toasty.success(
            'Data refreshed',
            'Charge type group data reloaded successfully.',
        );
    };

    render() {
        const { gridOptions, formValidated, selectedRows } = this.state;
        const selectionCount = (selectedRows ?? []).length
            ? ` (${selectedRows.length})`
            : '';

        return (
            <SlideForm
                size="col-xl-6 col-md-12 col-sm-12"
                loading={this.state.loading}
                show={this.state.show}
                id={this.props.id}
                formIcon={faFileInvoiceDollar}
                formTitle="Add Charge Type Groups to Contract"
                formRef={this.formRef}
                setIsValidated={(value) => {
                    this.setState({ formValidated: value });
                }}
                isValidated={formValidated}
                onClose={this.onClose}
            >
                <div className="w-100 d-flex flex-row align-items-center pt-1 pb-1">
                    <div className="flex-fill d-flex flex-row align-items-center">
                        <FontAwesomeIcon
                            icon={faQuestionCircle}
                            className="mr-1"
                        />
                        <small>
                            You may multiselect by clicking on individual rows.
                        </small>
                    </div>
                    <div className="flex-fill d-flex flex-row align-items-center justify-content-end">
                        <SmallButton
                            disabled={!!this.state.refreshing}
                            onClick={this.refreshChargeTypes}
                        >
                            <i className="fa fa-sync-alt fa-md mr-2" />
                            Refresh Charge Type Data
                        </SmallButton>
                    </div>
                </div>
                <div className="pt-0 flex-fill ag-theme-alpine datatable">
                    <AgGridReact
                        gridOptions={gridOptions}
                        rowData={this.state.rowData}
                    />
                </div>
                <Row className="d-flex flex-row justify-content-end pt-2 pl-0 pr-0 ml-0 mr-0 text-right w-100">
                    <Button
                        disabled={!(this.state.selectedRows ?? []).length}
                        size="sm"
                        type="button"
                        color="primary"
                        onClick={this.onAdd}
                    >
                        {`Add Selected${selectionCount}`}
                    </Button>
                </Row>
            </SlideForm>
        );
    }
}
